/**
 * Numbers and money in the person's display language — the portal's shapes, with the locale passed in.
 *
 * The portal's formatters read the portal's i18next singleton for the locale; the wetalk app has no such singleton, so
 * every function here takes the display language as an argument (docs/research/mobile-fonts-greek-intl.md §5.4). Only
 * `Intl.NumberFormat#format` is used: `formatToParts` is Android-only for numbers on Hermes and `notation`/`signDisplay`
 * are missing on iOS (research F3), so neither is reached.
 *
 * Money is integer minor units plus the currency the value carries (ADR 0014): the formatter is handed that currency,
 * so a value that ever stops being EUR renders as that currency rather than as a lie.
 */
/** The portal's mapping, exactly: Greek as `el-GR`, English as `en-GB`. */
export function intlLocaleOf(locale) {
    return locale === "el" ? "el-GR" : "en-GB";
}
const NUMBER_FORMAT = new Map();
function numberFormatOf(key, make) {
    const held = NUMBER_FORMAT.get(key);
    if (held !== undefined)
        return held;
    const made = make();
    NUMBER_FORMAT.set(key, made);
    return made;
}
function moneyFormatOf(locale, currency, minimumFractionDigits) {
    return numberFormatOf(`money:${locale}:${currency}:${String(minimumFractionDigits)}`, () => new Intl.NumberFormat(intlLocaleOf(locale), {
        style: "currency",
        currency,
        minimumFractionDigits,
        maximumFractionDigits: 2,
    }));
}
/** `€27.40` / `27,40 €`. Always two decimals — a total that reads `€27.4` looks like a bug. */
export function formatMoney(locale, currency, amountMinor) {
    return moneyFormatOf(locale, currency, 2).format(amountMinor / 100);
}
/** `€12` — whole units only (a round amount); cents only when there are any. */
export function formatWholeMoney(locale, currency, amountMinor) {
    const whole = amountMinor % 100 === 0;
    return moneyFormatOf(locale, currency, whole ? 0 : 2).format(amountMinor / 100);
}
/** `3,118` / `3.118` — the console's count grouping, no fraction. */
export function groupedCount(locale, value) {
    return numberFormatOf(`count:${locale}`, () => new Intl.NumberFormat(intlLocaleOf(locale), { maximumFractionDigits: 0 })).format(value);
}
//# sourceMappingURL=format.js.map